/* DeleteAction.js */

"use strict";

const PLAN_NAME_MAX_LENGTH = 60;
const PLAN_NAME_LIST_LENGTH = 10;

// ToDo - Add test for Status property values

class DeleteAction {
    constructor(logger, gameEditor, modalUserFeedback, activeView, userSession, OsUpgradePlan) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = this._gameEditor._modalUserFeedback;
        this._activeView = activeView;
        this._activeOrgId = userSession.activeOrganization.id;
        this._osPlan = OsUpgradePlan;
        this._planStatusLabels = this._osPlan.getStatusLabels() || [];
        this._selectedPlanIds = [];
        this._selectedPlanNames = [];

        if (this._activeView && this._activeView.pagingFeed) {
            this._selectedPlanIds = this._activeView.getSelectedPlanIds().selectedRows || [];
        }

        this.run = this.run.bind(this);
    }

    run() {
        if (this._selectedPlanIds.length) {
            this._activeView.pagingFeed
                .waitForRangeIdsUpdated()
                .then(() => {
                    if (this._selectedPlanIds.length <= PLAN_NAME_LIST_LENGTH) {
                        return this._activeView.pagingFeed.getIotOsPlanEntitiesNameAndStatusById(
                            this._selectedPlanIds
                        );
                    }
                    return Promise.resolve([]);
                })
                .then((selectedPlanNamesAndStatus) => {
                    this._selectedPlanNames =
                        selectedPlanNamesAndStatus.map((item) => item.name) || [];
                    this._selectedPlanStatus =
                        selectedPlanNamesAndStatus.map((item) => item.status) || [];
                    const isAnyPlanInProgress = this._selectedPlanStatus.some((status) => {
                        return ["VALIDATING", "BUNDLING", "UPLOADING_RESULTS"].includes(status);
                    });

                    const totalPlanCount = this._selectedPlanNames.length;
                    const planCount = Math.min(totalPlanCount, PLAN_NAME_LIST_LENGTH);
                    const planPluralization = totalPlanCount > 1 ? "s" : "";

                    if (totalPlanCount <= PLAN_NAME_LIST_LENGTH) {
                        this._message = `Are you sure you want to permanently delete the selected ${
                            totalPlanCount > 1 ? totalPlanCount : ""
                        } IoT OS plan${planPluralization}?\n`;

                        if (isAnyPlanInProgress) {
                            const planCountText =
                                totalPlanCount > 1 ? `Some of the plans` : "The plan";
                            this._message += `Note: ${planCountText} ${
                                totalPlanCount > 1 ? "are" : "is"
                            } in either VALIDATING, BUNDLING or UPLOADING RESULTS state. \n`;
                        }

                        for (let i = 0; i < planCount; i++) {
                            const planName = this._selectedPlanNames[i];
                            const shortPlanName =
                                planName.length > PLAN_NAME_MAX_LENGTH
                                    ? `${planName.substr(0, PLAN_NAME_MAX_LENGTH)}…`
                                    : planName;

                            this._message += `\n${shortPlanName}`;

                            if (isAnyPlanInProgress) {
                                const planStatus = this._selectedPlanStatus[i];
                                this._message += ` (${
                                    this._planStatusLabels[planStatus]
                                        ? this._planStatusLabels[planStatus].toUpperCase()
                                        : planStatus
                                })`;
                            }
                        }
                    } else {
                        this._message = `Are you sure you want to permanently delete the selected ${totalPlanCount} IoT OS plan${planPluralization}?\n`;
                        if (isAnyPlanInProgress) {
                            this._message +=
                                "\nNote: Some of the selected plans are in either VALIDATING, BUNDLING or UPLOADING RESULTS state.";
                        }
                    }

                    return Promise.resolve();
                })
                .then(() => {
                    return this._gameEditor.runQuickConfirmationActionWorkflow(
                        {
                            message: this._message,
                            title: "Delete",
                            confirmDetails: {
                                text: "Confirm",
                                type: "destructive",
                            },
                            rejectDetails: {
                                text: "Cancel",
                                type: "text",
                            },
                        },
                        () => {
                            let workTarget = {
                                method: "multi",
                                ids: this._selectedPlanIds,
                            };
                            const showProgFunc = (prog) => {
                                this._modalUserFeedback.showLoadingProgress(
                                    `${prog.successCount} of ${prog.targetCount}`
                                );
                            };
                            return this._osPlan
                                .runDeleteOdmOsUpgradePlansWork(
                                    this._activeOrgId,
                                    workTarget,
                                    undefined,
                                    showProgFunc
                                )
                                .then((workResults) => {
                                    this._osPlan.throwWorkErrors(workResults);
                                    this._activeView.resetSelection();
                                    if (
                                        this._activeView &&
                                        this._activeView.pagingFeed &&
                                        this._activeView.pagingFeed.feedUpdate
                                    ) {
                                        this._activeView.pagingFeed.feedUpdate();
                                    }
                                    return Promise.resolve();
                                });
                        }
                    );
                })
                .catch((err) => {
                    this._logger.warn({error: err}, "DeleteAction.run() - Error");
                    return this._modalUserFeedback.displayError({error: {errorMsg: err}});
                });
        } else {
            return Promise.resolve();
        }
    }
}

module.exports = DeleteAction;

DeleteAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "gameEditor.activeView",
    "$teve.userSession",
    "$teve.entities.OdmOsUpgradePlan", // Changed from OdmOsUpdatePlan
];

DeleteAction.$displayDetails = {
    type: "Action",
    name: "DeleteAction",
    icon: "common.trash_bin",
    shown: true,
    title: "Delete IoT OS Plans",
    order: 4,
};
